import type React from "react";
import { useEffect, useRef, useState } from "react";

import noop from "lodash/noop";

import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";

import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import I18n, { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberVisitsFilter } from "app/filters/ngFilters";
import { SELECT_ALL } from "app/UtilitiesAndConstants/Constants/multiSelect";

import { FooterButton } from "../../MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";

import type { IChipItem, IMultiChipDownProps } from "./MultiChipDownTypes";
import { Operator } from "./MultiChipDownTypes";

export const DropdownItem = (option: {
    text: string;
    id: string;
    tooltipText?: string;
    disabled?: boolean;
    disabledTooltip?: string;
    showCheckBox?: boolean;
}) => {
    const { id, text, tooltipText, disabled = false, disabledTooltip, showCheckBox = true } = option;
    return (
        <EllipsisDropdownItem
            showCheckBox={showCheckBox}
            key={id}
            id={id}
            tooltipText={tooltipText}
            disabled={disabled}
        >
            <PlainTooltip
                text={disabledTooltip}
                enabled={disabled ? Boolean(disabledTooltip) : false}
            >
                <span>{text}</span>
            </PlainTooltip>
        </EllipsisDropdownItem>
    );
};

export const getDropdownTitle = (dropdownTitles: string[], titleOperator = Operator.And) => {
    switch (dropdownTitles.length) {
        case 0:
            return "";
        case 1:
            return dropdownTitles[0];
        case 2:
            return translate(
                titleOperator === Operator.And
                    ? "multiselect.chipdown.filters.two.items"
                    : "multiselect.chipdown.filters.two.items.or",
                {
                    item1: dropdownTitles[0],
                    item2: dropdownTitles[1],
                },
            );
        case 3:
            return translate("multiselect.chipdown.filters.two.items.plus.extra.one", {
                item1: dropdownTitles[0],
                item2: dropdownTitles[1],
            });
        default:
            return translate("multiselect.chipdown.filters.two.items.plus.extra.items", {
                item1: dropdownTitles[0],
                item2: dropdownTitles[1],
                amount: dropdownTitles.length - 2,
            });
    }
};

const excludeAllFilter = ({ id }: FixmeType) => id !== SELECT_ALL;

const MultiChipDown: React.FC<IMultiChipDownProps> = ({
    options = [],
    initialSelectedItems,
    buttonText,
    onDone,
    appendTo = null,
    hasSearch = false,
    searchFilter,
    searchPlaceHolder = "",
    dropdownWidth = 320,
    dropdownPopupHeight = undefined,
    isDisabled = false,
    getDropdownItem,
    virtualize,
    maxVirtualItemsToRender,
    clearSelectionWhenOneOption = true,
    labelFooterButton,
    chipTooltip,
    renderChipTitleText,
    onToggleDropdown,
    titleOperator = Operator.And,
    onSelectItem,
    headerComponent,
    selectedText,
    shouldDisableApplyButton,
    trackingOnApply = noop,
    onItemClick = noop,
    cssClassContainer = "DropdownContent-container",
    getNoResultsComponent,
    onSearch,
    showTooltip = true,
}) => {
    const translate = useTranslation();
    const chipDownRef = useRef(undefined);
    const [selectedItems, setSelectedItems] = useState<IChipItem[]>([]);
    const [prevSelectedItems, setPrevSelectedItems] = useState<IChipItem[]>([]);
    const [showApplyButton, setShowApplyButton] = useState(false);
    const [chipTitle, setChipTitle] = useState<string>("");
    const [chipTooltipText, setChipTooltipText] = useState<string>("");
    const [selectedItemsIds, setSelectedItemsIds] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        if (options.length > 0) {
            // @ts-ignore FIXME: strict migration - Object.keys arg possibly undefined — initialSelectedItems is optional but callers provide it whenever options are supplied
            const items = options.filter((option) => Object.keys(initialSelectedItems).includes(option.id));
            setPrevSelectedItems(items);
            setSelectedItems(items);
        }
    }, [options]);

    useEffect(() => {
        setChipTooltipText(
            selectedItems
                .filter(excludeAllFilter)
                .map(({ text }) => text)
                .join(", "),
        );
        setSelectedItemsIds(Object.fromEntries(selectedItems.map((item) => [item.id, true])));
        setChipTitleText();
    }, [selectedItems]);

    const setChipTitleText = () => {
        const itemsTitles = selectedItems.map((item) => item?.text);

        if (typeof renderChipTitleText === "function") {
            renderChipTitleText(itemsTitles, setChipTitle);
            return;
        }

        setChipTitle(getDropdownTitle(itemsTitles, titleOperator));
    };

    const onApply = () => {
        onDone(selectedItems);
        trackingOnApply(selectedItems);
        setChipTitleText();
        setPrevSelectedItems(selectedItems);
        // @ts-ignore FIXME: strict migration - Property 'closePopup' does not exist on type 'never' — chipDownRef points at the untyped ChipDownContainer instance
        chipDownRef.current?.closePopup();
    };

    const onClick = (selectedItem: FixmeType) => {
        onItemClick(selectedItem, selectedItems);
        const selectedItemIndex = selectedItems.findIndex((item) => item.id === selectedItem.id);

        if (selectedItemIndex !== -1) {
            if (selectedItem.id === SELECT_ALL) {
                setSelectedItems([]);
            } else {
                setSelectedItems((state) =>
                    [...state.slice(0, selectedItemIndex), ...state.slice(selectedItemIndex + 1)].filter(
                        excludeAllFilter,
                    ),
                );
            }
        } else {
            const selectedOption = options.find((option) => option.id === selectedItem.id);
            // @ts-ignore FIXME: strict migration - 'selectedOption' possibly undefined — selectedItem.id always corresponds to an existing option
            onSelectItem && onSelectItem(selectedOption);
            // @ts-ignore FIXME: strict migration - 'selectedOption' possibly undefined — selectedItem.id always corresponds to an existing option
            if (selectedOption.id === SELECT_ALL) {
                setSelectedItems(options);
            } else {
                // @ts-ignore FIXME: strict migration - 'selectedOption' possibly undefined — selectedItem.id always corresponds to an existing option
                setSelectedItems((state) => [...state, selectedOption]);
            }
        }
        setShowApplyButton(true);
    };

    const onToggle = (isOpen: boolean, isOutsideClick: boolean) => {
        if (typeof onToggleDropdown === "function") {
            onToggleDropdown(isOpen);
        }

        if (!isOpen) {
            setShowApplyButton(false);

            if (onSearch) {
                onSearch("");
            }

            if (isOutsideClick) {
                setSelectedItems(prevSelectedItems);
            }
            if (clearSelectionWhenOneOption && selectedItems.length === options.length) {
                // If user closed the dropdown and selected all options - clear selection
                clearSelection();
            }
        }
    };

    const clearSelection = () => {
        setSelectedItems([]);
        setPrevSelectedItems([]);
        onDone([]);
        setChipTitle("");
    };

    return (
        <ChipDownContainer
            ref={chipDownRef}
            tooltipDisabled={!showTooltip || selectedItems.length === 1}
            selectedText={selectedText ?? chipTitle}
            tooltipText={chipTooltip ?? chipTooltipText}
            closeOnItemClick={false}
            onToggle={onToggle}
            onCloseItem={clearSelection}
            selectedIds={selectedItemsIds}
            width={dropdownWidth}
            dropdownPopupHeight={dropdownPopupHeight}
            buttonText={buttonText}
            noResultsMessage={translate("dropdown.no.results")}
            hasSearch={hasSearch}
            onSearch={onSearch}
            // @ts-ignore FIXME: strict migration - searchFilter item type IChipItem vs ChipDownContainer's IDropdownChild — external component type
            searchFilter={searchFilter}
            searchPlaceHolder={searchPlaceHolder}
            headerComponent={headerComponent}
            footerComponent={() =>
                showApplyButton && (
                    <FooterButton
                        onClick={onApply}
                        disabled={shouldDisableApplyButton ? shouldDisableApplyButton(selectedItems) : false}
                    >
                        {labelFooterButton || <I18n>common.apply</I18n>}
                    </FooterButton>
                )
            }
            cssClassContainer={cssClassContainer}
            disabled={isDisabled}
            getNoResultsComponent={getNoResultsComponent}
            onClick={onClick}
            virtualize={virtualize}
            maxVirtualItemsToRender={maxVirtualItemsToRender}
            // @ts-ignore FIXME: strict migration - Type 'string | null' is not assignable to 'string | undefined' — external ChipDownContainer appendTo prop
            appendTo={appendTo}
        >
            {options.map((option) =>
                getDropdownItem ? (
                    getDropdownItem(option)
                ) : (
                    <EllipsisDropdownItem
                        showCheckBox
                        key={option.id}
                        id={option.id}
                        infoText={abbrNumberVisitsFilter()(option?.count || 0)}
                        imageUrl={option?.imageUrl}
                        disabled={option?.count == 0}
                    >
                        {translate(option.text)}
                    </EllipsisDropdownItem>
                ),
            )}
        </ChipDownContainer>
    );
};

export default MultiChipDown;
